import { ethers } from 'ethers';
import {
  HL_INFO_URL, HL_EXCHANGE_URL,
  HL_DOMAIN, HL_AGENT_TYPES, HL_SLIPPAGE,
} from './hyperliquidConstants';

/* global BigInt */

// ─────────────────────────────────────────────────────────────────────────────
// Minimal msgpack encoder — matches Python SDK's msgpack.packb output exactly.
// ─────────────────────────────────────────────────────────────────────────────
function encode(value) {
  const chunks = [];
  _enc(value, chunks);
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}
function _enc(v, out) {
  if (v === null || v === undefined) { out.push(new Uint8Array([0xc0])); return; }
  if (typeof v === 'boolean') { out.push(new Uint8Array([v ? 0xc3 : 0xc2])); return; }
  if (typeof v === 'number') { Number.isInteger(v) ? _encInt(v, out) : _encF64(v, out); return; }
  if (typeof v === 'string') { _encStr(v, out); return; }
  if (Array.isArray(v))      { _encArr(v, out); return; }
  if (typeof v === 'object') { _encMap(v, out); return; }
}
function _encInt(n, out) {
  if (n >= 0 && n <= 0x7f) { out.push(new Uint8Array([n])); return; }
  if (n < 0 && n >= -32)   { out.push(new Uint8Array([0xe0 | (n + 32)])); return; }
  const b = new Uint8Array(9); const v = new DataView(b.buffer);
  if (n >= 0) {
    if (n <= 0xff)            { b[0]=0xcc; v.setUint8(1,n);            out.push(b.slice(0,2)); }
    else if (n <= 0xffff)     { b[0]=0xcd; v.setUint16(1,n,false);     out.push(b.slice(0,3)); }
    else if (n <= 0xffffffff) { b[0]=0xce; v.setUint32(1,n,false);     out.push(b.slice(0,5)); }
    else                      { b[0]=0xcf; v.setBigUint64(1,BigInt(n),false); out.push(b.slice(0,9)); }
  } else {
    if (n >= -128)            { b[0]=0xd0; v.setInt8(1,n);             out.push(b.slice(0,2)); }
    else if (n >= -32768)     { b[0]=0xd1; v.setInt16(1,n,false);      out.push(b.slice(0,3)); }
    else if (n >= -2147483648){ b[0]=0xd2; v.setInt32(1,n,false);      out.push(b.slice(0,5)); }
    else                      { b[0]=0xd3; v.setBigInt64(1,BigInt(n),false); out.push(b.slice(0,9)); }
  }
}
function _encF64(n, out) {
  const b = new Uint8Array(9); b[0] = 0xcb;
  new DataView(b.buffer).setFloat64(1, n, false);
  out.push(b);
}
function _encStr(s, out) {
  const bytes = new TextEncoder().encode(s);
  const len = bytes.length;
  if (len <= 31)          { out.push(new Uint8Array([0xa0 | len])); }
  else if (len <= 0xff)   { out.push(new Uint8Array([0xd9, len])); }
  else if (len <= 0xffff) { const h = new Uint8Array(3); h[0]=0xda; new DataView(h.buffer).setUint16(1,len,false); out.push(h); }
  else                    { const h = new Uint8Array(5); h[0]=0xdb; new DataView(h.buffer).setUint32(1,len,false); out.push(h); }
  out.push(bytes);
}
function _encArr(a, out) {
  const len = a.length;
  if (len <= 15)          { out.push(new Uint8Array([0x90 | len])); }
  else if (len <= 0xffff) { const h = new Uint8Array(3); h[0]=0xdc; new DataView(h.buffer).setUint16(1,len,false); out.push(h); }
  else                    { const h = new Uint8Array(5); h[0]=0xdd; new DataView(h.buffer).setUint32(1,len,false); out.push(h); }
  for (const item of a) _enc(item, out);
}
function _encMap(obj, out) {
  const keys = Object.keys(obj);
  const len  = keys.length;
  if (len <= 15)          { out.push(new Uint8Array([0x80 | len])); }
  else if (len <= 0xffff) { const h = new Uint8Array(3); h[0]=0xde; new DataView(h.buffer).setUint16(1,len,false); out.push(h); }
  else                    { const h = new Uint8Array(5); h[0]=0xdf; new DataView(h.buffer).setUint32(1,len,false); out.push(h); }
  for (const k of keys) { _enc(k, out); _enc(obj[k], out); }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// Matches Python SDK's float_to_wire: up to 8 decimals, trailing zeros stripped
function floatToWire(x) {
  let s = x.toFixed(8);
  if (s.includes('.')) s = s.replace(/\.?0+$/, '');
  return s;
}

// Matches Python SDK's round_to_sig_figs — rounds price to (5 - szDecimals) sig figs
// so the price is divisible by HL's tick size for that asset.
function roundToSigFigs(value, sigFigs) {
  if (value === 0) return 0;
  const d = Math.ceil(Math.log10(Math.abs(value)));
  const factor = Math.pow(10, sigFigs - d);
  return Math.round(value * factor) / factor;
}

function priceToWire(px, szDecimals) {
  // HL formula is (5 - szDecimals) sig figs, but clamp to ≥2 so high-szDecimals
  // assets (BTC is now szDecimals=5) don't round to the nearest $100,000.
  return floatToWire(roundToSigFigs(px, Math.max(2, 5 - szDecimals)));
}

// Format price for display
export function fmtPrice(px, coin) {
  if (!px) return '—';
  const n = parseFloat(px);
  if (coin === 'BTC') return n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (n >= 1000)      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n >= 1)         return n.toFixed(4);
  return n.toFixed(6);
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent management
// Per the HL docs: approve an ephemeral agent wallet once (signed by main wallet
// with current chainId so Rabby accepts it), then all orders are signed by the
// local agent key — no wallet popup per trade, no chainId conflict.
// ─────────────────────────────────────────────────────────────────────────────

const HL_APPROVE_AGENT_TYPES = {
  'HyperliquidTransaction:ApproveAgent': [
    { name: 'hyperliquidChain', type: 'string'  },
    { name: 'agentAddress',     type: 'address' },
    { name: 'agentName',        type: 'string'  },
    { name: 'nonce',            type: 'uint64'  },
  ],
};

const _agentCache = {}; // address → ethers.Wallet, lives for the page session

export async function getOrCreateAgent(signer, address) {
  const key = address.toLowerCase();
  if (_agentCache[key]) return _agentCache[key];

  const stored = sessionStorage.getItem(`hl_agent_${key}`);
  if (stored) {
    const w = new ethers.Wallet(stored);
    _agentCache[key] = w;
    console.log('[HL agent] restored from sessionStorage:', w.address);
    return w;
  }

  // Generate new ephemeral agent and approve it on HL
  const agentWallet = ethers.Wallet.createRandom();
  const nonce       = Date.now();
  const { chainId } = await signer.provider.getNetwork();

  // Action sent to HL (includes signatureChainId so HL knows which domain we used)
  const action = {
    type:              'approveAgent',
    hyperliquidChain:  'Mainnet',
    signatureChainId:  '0x' + chainId.toString(16),
    agentAddress:      agentWallet.address.toLowerCase(),
    agentName:         '',
    nonce,
  };

  // Domain uses wallet's current chainId → Rabby accepts it
  const domain = {
    name:              'HyperliquidSignTransaction',
    version:           '1',
    chainId,
    verifyingContract: '0x0000000000000000000000000000000000000000',
  };

  // Signed message omits type + signatureChainId (HL reads those from action payload)
  const message = {
    hyperliquidChain: 'Mainnet',
    agentAddress:     agentWallet.address.toLowerCase(),
    agentName:        '',
    nonce,
  };

  const rawSig = await signer._signTypedData(domain, HL_APPROVE_AGENT_TYPES, message);
  const sig    = ethers.utils.splitSignature(rawSig);

  const res = await fetch(HL_EXCHANGE_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ action, nonce, signature: sig }),
  });
  if (!res.ok) throw new Error(`approveAgent HTTP ${res.status}`);
  const data = await res.json();
  console.log('[HL agent] approveAgent response:', JSON.stringify(data));
  if (data.status === 'err') throw new Error(data.response || 'approveAgent failed');

  console.log('[HL agent] approved:', agentWallet.address);
  sessionStorage.setItem(`hl_agent_${key}`, agentWallet.privateKey);
  _agentCache[key] = agentWallet;
  return agentWallet;
}

// Security: purge every agent private key — in-memory cache AND sessionStorage — so a deliberate
// wallet disconnect leaves no trading key behind (reduces the XSS blast radius). Call on disconnect.
export function clearAgents() {
  for (const k of Object.keys(_agentCache)) delete _agentCache[k];
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith('hl_agent_')) sessionStorage.removeItem(k);
    }
  } catch { /* sessionStorage unavailable — cache already cleared */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Signing — agent wallet signs L1 actions directly (no wallet popup)
// ─────────────────────────────────────────────────────────────────────────────

function buildActionHash(action, vaultAddress, nonce) {
  const packed   = encode(action);
  const nonceBuf = new Uint8Array(8);
  new DataView(nonceBuf.buffer).setBigUint64(0, BigInt(nonce), false);

  const parts = [packed, nonceBuf];
  if (vaultAddress == null) {
    parts.push(new Uint8Array([0x00]));
  } else {
    parts.push(new Uint8Array([0x01]));
    parts.push(ethers.utils.arrayify(vaultAddress));
  }

  const total  = parts.reduce((n, p) => n + p.length, 0);
  const merged = new Uint8Array(total);
  let offset   = 0;
  for (const p of parts) { merged.set(p, offset); offset += p.length; }

  return ethers.utils.keccak256(merged);
}

// agentWallet is an ethers.Wallet (local key) — signs with HL_DOMAIN (chainId 1337)
// without any browser wallet popup or chainId restriction.
async function signL1Action(agentWallet, action, nonce, vaultAddress = null) {
  const hash         = buildActionHash(action, vaultAddress, nonce);
  const phantomAgent = { source: 'a', connectionId: hash };
  const sig          = await agentWallet._signTypedData(HL_DOMAIN, HL_AGENT_TYPES, phantomAgent);
  return ethers.utils.splitSignature(sig);
}

// ─────────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────────

async function hlPost(payload) {
  const res = await fetch(HL_INFO_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`HL info ${res.status}`);
  return res.json();
}

// agentWallet signs the action — no wallet extension involved
async function hlExchange(agentWallet, action) {
  const nonce = Date.now();
  const sig   = await signL1Action(agentWallet, action, nonce);

  console.log('[HL exchange] nonce:', nonce, '| action:', JSON.stringify(action));

  const res = await fetch(HL_EXCHANGE_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ action, nonce, signature: sig }),
  });
  if (!res.ok) throw new Error(`HL exchange ${res.status}`);
  const data = await res.json();

  console.log('[HL exchange] raw response:', JSON.stringify(data));

  if (data.status === 'err') throw new Error(data.response || 'HL exchange error');

  const statuses = data.response?.data?.statuses;
  if (statuses?.[0]?.error) throw new Error(statuses[0].error);

  return data.response;
}

// ─────────────────────────────────────────────────────────────────────────────
// Market data
// ─────────────────────────────────────────────────────────────────────────────

// Returns { assets: [{name, szDecimals, maxLeverage, index}], ctxs: [{markPx, funding, openInterest}] }
export async function fetchMeta() {
  const [meta, ctxs] = await hlPost({ type: 'metaAndAssetCtxs' });
  const assets = meta.universe.map((u, i) => ({
    name:        u.name,
    szDecimals:  u.szDecimals,
    maxLeverage: u.maxLeverage,
    onlyIsolated: u.onlyIsolated || false,
    index:       i,
  }));
  const contexts = ctxs.map(c => ({
    markPx:      c.markPx,
    midPx:       c.midPx,
    funding:     c.funding,
    openInterest: c.openInterest,
    dayNtlVlm:   c.dayNtlVlm,
  }));
  return { assets, contexts };
}

// ─────────────────────────────────────────────────────────────────────────────
// Account state
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_ACCOUNT  = { positions: [], accountValue: 0,    withdrawable: 0,    marginSummary: null, notRegistered: true  };
const FETCH_ERROR   = { positions: [], accountValue: null, withdrawable: null, marginSummary: null, notRegistered: false };

// Returns { positions, accountValue, withdrawable, marginSummary, notRegistered? }
export async function fetchAccountState(address) {
  let data;
  try {
    data = await hlPost({ type: 'clearinghouseState', user: address.toLowerCase() });
  } catch (e) {
    console.warn('[HL] clearinghouseState fetch error:', e.message);
    return FETCH_ERROR; // network / HTTP error — don't treat as unregistered
  }

  // Log the actual values so we can see what HL returns
  console.log('[HL] clearinghouseState type:', typeof data);
  if (typeof data !== 'string') {
    console.log('[HL] marginSummary.accountValue:', data?.marginSummary?.accountValue, '| withdrawable:', data?.withdrawable);
  }

  // HL returns a plain string when the address has never deposited
  if (typeof data === 'string') return EMPTY_ACCOUNT;
  if (data?.error)              return EMPTY_ACCOUNT;

  const positions = (data.assetPositions || [])
    .filter(ap => parseFloat(ap.position?.szi) !== 0)
    .map(ap => {
      const p = ap.position;
      const szi = parseFloat(p.szi);
      return {
        coin:          p.coin,
        szi,
        isLong:        szi > 0,
        sizeAbs:       Math.abs(szi),
        entryPx:       parseFloat(p.entryPx),
        positionValue: parseFloat(p.positionValue),
        unrealizedPnl: parseFloat(p.unrealizedPnl),
        liquidationPx: p.liquidationPx ? parseFloat(p.liquidationPx) : null,
        leverage:      p.leverage,
        returnOnEquity: parseFloat(p.returnOnEquity || 0),
      };
    });

  // Also fetch spot balances (separate account on HL — e.g. HYPE airdrop, spot USDC)
  let spotBalances = [];
  try {
    const spotData = await hlPost({ type: 'spotClearinghouseState', user: address.toLowerCase() });
    if (Array.isArray(spotData?.balances)) {
      spotBalances = spotData.balances
        .filter(b => parseFloat(b.total) > 0)
        .map(b => ({ coin: b.coin, token: b.token, total: parseFloat(b.total) }));
      console.log('[HL] spot balances:', JSON.stringify(spotBalances));
    }
  } catch (e) {
    console.warn('[HL] spot fetch error:', e.message);
  }

  return {
    positions,
    accountValue:  parseFloat(data.marginSummary?.accountValue || 0),
    withdrawable:  parseFloat(data.withdrawable || 0),
    marginSummary: data.marginSummary,
    spotBalances,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Order actions
// ─────────────────────────────────────────────────────────────────────────────

// Set leverage for an asset before placing an order.
// isCross=true for cross margin (recommended for new users).
export async function updateLeverage(agentWallet, assetIndex, leverage, isCross = true) {
  const action = {
    type:     'updateLeverage',
    asset:    assetIndex,
    isCross,
    leverage: Math.round(leverage),
  };
  return hlExchange(agentWallet, action);
}

// Place a market order (IOC limit with slippage price).
// coin     — asset name string (e.g. 'BTC') used to fetch the live L2 book
// sizeUsd  — POSITION size (collateral × leverage)
// markPx   — fallback price if the book fetch fails
export async function placeMarketOrder(agentWallet, { assetIndex, coin, isBuy, sizeUsd, markPx, szDecimals }) {
  const markPrice = parseFloat(markPx);
  const sizeAsset = (sizeUsd / markPrice).toFixed(szDecimals);

  // Coin size rounds to zero — order notional is below the asset's minimum lot
  const minCoinSize = Math.pow(10, -szDecimals);
  if (parseFloat(sizeAsset) < minCoinSize) {
    const minUsd = (minCoinSize * markPrice).toFixed(0);
    throw new Error(`Too small: need ~$${minUsd} position (${minCoinSize} min lot). Increase leverage or collateral.`);
  }

  // Fetch live orderbook so the IOC limit is anchored to the ACTUAL best ask/bid,
  // not a stale captured mark price that may have moved across a tick boundary.
  let anchorPx = markPrice;
  let bookSource = 'markPx (fallback)';
  try {
    const book = await hlPost({ type: 'l2Book', coin });
    console.log('[HL order] l2Book raw:', JSON.stringify(book).slice(0, 300));
    // levels[0] = bids (best bid first, descending), levels[1] = asks (best ask first, ascending)
    const levels = isBuy ? book.levels[1] : book.levels[0];
    if (levels && levels.length > 0) {
      anchorPx   = parseFloat(levels[0].px);
      bookSource = `l2Book (${isBuy ? 'ask' : 'bid'}[0])`;
    }
  } catch (e) {
    console.warn('[HL order] l2Book fetch failed:', e.message, '— using markPx');
  }

  const limitPx   = isBuy ? anchorPx * (1 + HL_SLIPPAGE) : anchorPx * (1 - HL_SLIPPAGE);
  const priceWire = priceToWire(limitPx, szDecimals);

  console.log(`[HL order] isBuy=${isBuy} | anchor=${anchorPx} (${bookSource}) | limitPx=${limitPx} | priceWire=${priceWire} | sizeAsset=${sizeAsset}`);

  const orderWire = {
    a: assetIndex,
    b: isBuy,
    p: priceWire,
    s: sizeAsset,
    r: false,
    t: { limit: { tif: 'Ioc' } },
  };

  const action = {
    type:     'order',
    orders:   [orderWire],
    grouping: 'na',
  };

  try {
    return await hlExchange(agentWallet, action);
  } catch (e) {
    // Re-throw with wire values visible so the UI status shows what was sent
    throw new Error(`${e.message} [p=${priceWire} s=${sizeAsset}]`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// USDC deposit to Hyperliquid via EIP-3009 ReceiveWithAuthorization on Arbitrum
// ─────────────────────────────────────────────────────────────────────────────

const USDC_ARB      = '0xaf88d065e77c8cC2239327C5EDb3A432268e5831';
const HL_BRIDGE_ARB = '0xA95d9c1F655341597C94393fDdc30cf3c08E4fcE';
export const ARB_CHAIN_ID = 42161;

const USDC_DOMAIN = {
  name: 'USD Coin', version: '2', chainId: ARB_CHAIN_ID, verifyingContract: USDC_ARB,
};
const RECEIVE_WITH_AUTH_TYPES = {
  ReceiveWithAuthorization: [
    { name: 'from',        type: 'address' },
    { name: 'to',          type: 'address' },
    { name: 'value',       type: 'uint256' },
    { name: 'validAfter',  type: 'uint256' },
    { name: 'validBefore', type: 'uint256' },
    { name: 'nonce',       type: 'bytes32' },
  ],
};
const USDC_ABI = [
  'function receiveWithAuthorization(address from, address to, uint256 value, uint256 validAfter, uint256 validBefore, bytes32 nonce, uint8 v, bytes32 r, bytes32 s) external',
];

// Deposits amountUsdc (e.g. 10.5) from Arbitrum USDC to HL.
// Wallet must be on Arbitrum (chainId 42161) before calling.
export async function depositUsdcToHL(signer, address, amountUsdc) {
  const value       = BigInt(Math.round(amountUsdc * 1e6));
  const now         = Math.floor(Date.now() / 1000);
  const validAfter  = now - 60;
  const validBefore = now + 3600;
  const nonce       = ethers.utils.hexlify(ethers.utils.randomBytes(32));
  const message     = { from: address, to: HL_BRIDGE_ARB, value: value.toString(), validAfter, validBefore, nonce };

  const sig         = await signer._signTypedData(USDC_DOMAIN, RECEIVE_WITH_AUTH_TYPES, message);
  const { v, r, s } = ethers.utils.splitSignature(sig);

  const usdc = new ethers.Contract(USDC_ARB, USDC_ABI, signer);
  return usdc.receiveWithAuthorization(address, HL_BRIDGE_ARB, value, validAfter, validBefore, nonce, v, r, s);
}

// Close an open position fully (reduceOnly market order).
export async function closePosition(agentWallet, { assetIndex, coin, isLong, sizeAbs, markPx, szDecimals }) {
  const markPrice = parseFloat(markPx);
  const isBuy     = !isLong; // close long = sell, close short = buy

  let anchorPx = markPrice;
  try {
    const book   = await hlPost({ type: 'l2Book', coin });
    const levels = isBuy ? book.levels[1] : book.levels[0];
    if (levels && levels.length > 0) anchorPx = parseFloat(levels[0].px);
  } catch (_) { /* fall back to markPrice */ }

  const limitPx = isBuy
    ? anchorPx * (1 + HL_SLIPPAGE)
    : anchorPx * (1 - HL_SLIPPAGE);

  const orderWire = {
    a: assetIndex,
    b: isBuy,
    p: priceToWire(limitPx, szDecimals),
    s: sizeAbs.toFixed(szDecimals),
    r: true, // reduceOnly
    t: { limit: { tif: 'Ioc' } },
  };

  const action = {
    type:     'order',
    orders:   [orderWire],
    grouping: 'na',
  };

  return hlExchange(agentWallet, action);
}
